import clsx from 'clsx'
import { isValidJson } from 'components/form/utilities'
import { updateTemplate, useDataTemplate } from 'components/form_builder/features/data/dataSlice'
import { updateHtmlNodes } from 'components/form_builder/features/fields/fieldsSlice'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import Input from '../../Input'
import useDragAndDropPaths from '../../hooks/useDragAndDropPaths'
import { useMultiplePaths } from '../../hooks/useMultiplePaths'
import {
  extractAllSections,
  findAndInsertNode,
  findAndRemoveQuestionFromPath,
  findSectionFromField,
} from '../../utility'

const LIST_TOGGLE = [
  {
    id: 'list',
    label: 'list',
    component: <i className="fa-solid fa-list"></i>,
  },
  {
    id: 'code',
    label: 'code',
    component: <i className="fa-solid fa-code"></i>,
  },
]

export const ToggleSwitch = ({ selected, options, onSelect }) => {
  const selectedIndex = options.findIndex((option) => option.id === selected)
  const numberOfOptions = options.length

  const sliderStyle = {
    width: `${100 / numberOfOptions}%`,
    transform: `translateX(${selectedIndex * 100}%)`,
  }

  return (
    <div className="toggle-switch-container">
      {options?.map(({ id, component }) => (
        <button
          key={id}
          type="button"
          className="toggle-option"
          onClick={() => onSelect(id)}
          aria-selected={id === selected}
          style={{ width: sliderStyle.width }}
        >
          {component}
        </button>
      ))}
      <div className="toggle-slider" style={sliderStyle}></div>
    </div>
  )
}

export const LinkedPaths = ({ label = 'linked paths', linkedPaths }) => {
  if (!linkedPaths || !linkedPaths.length) return null
  return (
    <div className="dome-d-flex dome-flex-column dome-gap6">
      <span className="dome-p3 dome-color-med-grey">{label}</span>
      <ul style={{ padding: 0, margin: 0, listStyle: 'none' }} className="dome-d-flex dome-flex-column dome-gap6">
        {linkedPaths?.map((path) => {
          return (
            <li key={path} className="dome-flex-1">
              <Input name={path} value={path} readOnly={true}/>
              <SectionItem path={path} />
            </li>
          )
        })}
      </ul>
    </div>
  )
}

const TextArea = ({ component }) => {
  const dispatch = useDispatch()
  const template = useDataTemplate(component.type, component.id)
  const textAreaRef = useRef(null)

  const [input, setInput] = useState('')
  const isValid = useMemo(() => isValidJson(input), [input])

  useEffect(() => {
    if (template.paths?.length) {
      const paths = template.paths.map((e) => e.path)
      const pathsString = JSON.stringify(paths, null, paths.length)
      setInput(pathsString)
    }
  }, [template.paths])

  const handleInputChange = (e) => setInput(e.target.value)

  const storeIfValid = useCallback(() => {
    try {
      if (!isValidJson(input)) return
      const curPaths = JSON.parse(input)
      const templatePaths = new Set(template.templatePaths)
      const newPaths = curPaths.map((path) => ({ path, type: templatePaths.has(path) ? 'templatePath' : 'fillPath' }))
      dispatch(updateTemplate({ id: component.id, type: component.type, template: { ...template, paths: newPaths } }))
    } catch (e) {}
  }, [input, component, template])

  const handleKeyDown = useCallback(
    (e) => {
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyS') {
        e.preventDefault()
        storeIfValid()
      }
      if (e.target === textAreaRef.current) e.stopPropagation()
    },
    [storeIfValid]
  )

  return (
    <textarea
      ref={textAreaRef}
      value={input || ''}
      className="scrollable"
      onBlur={storeIfValid}
      onKeyDown={handleKeyDown}
      onChange={handleInputChange}
      style={{ borderColor: isValid ? '#898989' : '#d7272d' }}
    />
  )
}

const ActionIcon = ({ onClick, readOnly, iconClassName }) => {
  return (
    <div
      style={{
        width: 20,
        height: 20,
        borderRadius: 30,
        background: readOnly ? 'rgba(194, 220, 224, 0.30)' : 'white',
      }}
      onClick={onClick}
      className="dome-d-flex dome-align-center dome-justify-center dome-cursor-pointer"
    >
      <i className={iconClassName}></i>
    </div>
  )
}

export const SectionItem = ({ path }) => {
  const ref = useRef()
  const dispatch = useDispatch()
  const [openSectionMenu, setOpenSectionMenu] = useState(false)
  const [openSectionInput, setOpenSectionInput] = useState(false)

  const { html_nodes, field_defs } = useSelector((state) => state.fields)
  const sections = extractAllSections(html_nodes)
  const activeSection = findSectionFromField(html_nodes, path)

  const field = field_defs[path]

  const addNewSection = (value) => {
    const newSection = {
      type: 'section',
      section_attributes: {
        label: value,
      },
      nodes: [],
      id: uuidv4(),
    }
    const newHtmlNodes = JSON.parse(JSON.stringify(html_nodes))
    newHtmlNodes.push(newSection)
    dispatch(updateHtmlNodes({ html_nodes: newHtmlNodes }))
  }

  const unassignSection = () => {
    const newHtmlNodes = JSON.parse(JSON.stringify(html_nodes))
    findAndRemoveQuestionFromPath(newHtmlNodes, path)
    dispatch(updateHtmlNodes({ html_nodes: newHtmlNodes }))
  }

  const assignQuestionToPath = ({ section }) => {
    const newHtmlNodes = JSON.parse(JSON.stringify(html_nodes))
    findAndRemoveQuestionFromPath(newHtmlNodes, path)
    const question = {
      type: 'question',
      question_attributes: {
        field: path,
        label: field.label,
        question_label: field.question_label,
      },
      nodes: [],
      id: uuidv4(),
    }
    findAndInsertNode(newHtmlNodes, section.id, question, section?.nodes?.length || 0)
    dispatch(updateHtmlNodes({ html_nodes: newHtmlNodes }))
  }

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpenSectionMenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey) {
      if (e.code === 'KeyS') {
        e.preventDefault()
        e.target.blur()
      }
    }
    if (e.code === 'Enter') e.target.blur()
  }

  if (!field) return

  return (
    <div
      ref={ref}
      style={{
        padding: '3px 12px',
        border: '1px solid #B7B7B7',
        marginTop: '-11px',
        borderRadius: '0 0 6px 6px',
        background: 'var(--accent, #F0F0F0)',
        position: 'relative',
      }}
    >
      <div className="dome-d-flex dome-align-center">
        <div className="dome-flex-1">
          <span className="dome-p2 dome-color-med-grey">section: </span>
          {activeSection ? (
            <span className="dome-p2 dome-color-darkest-grey">{activeSection?.section_attributes?.label}</span>
          ) : (
            <span className="dome-p2 dome-color-med-grey">unassigned</span>
          )}
        </div>
        <i
          onClick={() => setOpenSectionMenu((pre) => !pre)}
          className="fa-regular fa-chevron-down dome-color-dark-grey dome-cursor-pointer"
        ></i>
      </div>
      {openSectionMenu && (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            transform: 'translateY(100%)',
            bottom: '-3px',
            left: 0,
            background: 'white',
            padding: '0px 12px 0px 12px',
            zIndex: 1000,
            borderRadius: 6,
            boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
          }}
        >
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} className="dome-d-flex dome-flex-column">
            <li onClick={unassignSection} style={{ padding: '6px 0px 6px 0px', borderBottom: '1px solid #DBDBDB' }}>
              <div className="dome-d-flex dome-gap6 dome-cursor-pointer">
                <span className={clsx('dome-p2', activeSection ? 'dome-color-med-grey' : 'dome-color-darkest-grey')}>
                  unassigned section
                </span>
                <span className="dome-p2 dome-color-med-grey">(default)</span>
              </div>
            </li>
            {sections.map((section) => {
              const label = section.section_attributes?.label
              const active = activeSection?.id === section.id
              return (
                <li
                  key={section.id}
                  onClick={() => assignQuestionToPath({ section })}
                  style={{ padding: '6px 0px 6px 0px', borderBottom: '1px solid #DBDBDB', cursor: 'pointer' }}
                >
                  <span className={clsx('dome-p2', active ? 'dome-color-darkest-grey' : 'dome-color-med-grey')}>
                    {label}
                  </span>
                </li>
              )
            })}
            {openSectionInput && (
              <li style={{ padding: '6px 0px 6px 0px', borderBottom: '1px solid #DBDBDB' }}>
                <input
                  type="text"
                  style={{ border: 'none', outline: 'none', width: '100%' }}
                  onKeyDown={handleKeyDown}
                  onBlur={(e) => {
                    setOpenSectionInput(false)
                    addNewSection(e.target.value)
                  }}
                  placeholder="name your new section"
                />
              </li>
            )}
            <li
              onClick={() => setOpenSectionInput(true)}
              style={{ padding: '6px 0px 6px 0px' }}
              className="dome-p2 dome-color-aqua dome-text-w500 dome-cursor-pointer"
            >
              + new section
            </li>
          </ul>
        </div>
      )}
    </div>
  )
}

export const PathListItems = ({
  paths,
  addNewPath,
  removePath,
  onChange,
  onBlur,
  onKeyUp,
  onKeyDown,
  onDragStart,
  onDragEnter,
  onDragEnd,
}) => {
  return (
    <div className="dome-d-flex dome-flex-column dome-gap6">
      <div className="dome-d-flex dome-flex-column dome-gap6">
        {paths?.map(({ type, path }, index) => {
          const readOnly = type === 'templatePath'
          const iconClassName =
            type === 'templatePath' ? 'fa-solid fa-minus dome-color-aqua' : 'fa-regular fa-xmark dome-color-red-alert'
          return (
            <div
              key={index}
              draggable="true"
              onDragEnd={() => onDragEnd(index)}
              onDragOver={(e) => e.preventDefault()}
              onDragStart={() => onDragStart(index)}
              onDragEnter={() => onDragEnter(index)}
              className="dome-d-flex dome-gap6 dome-align-start"
            >
              <span style={{ cursor: 'move' }}>
                <i className="fa-light fa-grip-vertical dome-color-med-grey"></i>
              </span>
              <div className="dome-flex-1">
                <Input
                  name={path}
                  value={path}
                  onBlur={onBlur}
                  onKeyUp={onKeyUp}
                  onKeyDown={onKeyDown}
                  readOnly={readOnly}
                  placeholder="type or paste path"
                  onChange={(e) => onChange(e.target.value, index)}
                  append={
                    <ActionIcon readOnly={readOnly} onClick={() => removePath(index)} iconClassName={iconClassName} />
                  }
                />
                <SectionItem path={path} />
              </div>
            </div>
          )
        })}
      </div>
      <div
        onClick={addNewPath}
        style={{ width: 'fit-content' }}
        className="dome-p2 dome-color-aqua dome-cursor-pointer"
      >
        + add another
      </div>
    </div>
  )
}

export const FillOnlyPaths = ({ id, component }) => {
  const dispatch = useDispatch()
  const template = useDataTemplate(...id)
  const [activeOption, setActiveOption] = useState('list')

  const handleSave = useCallback(
    (newPaths) => {
      dispatch(
        updateTemplate({
          id: component.id,
          type: component.type,
          template: { ...template, paths: newPaths },
        })
      )
    },
    [dispatch, component.id, component.type, template]
  )

  const initialPaths = useMemo(() => template.paths || [], [template])

  const { paths, setPaths, handleChange, handleRemove, handleAdd, handleBlur, handleKeyDown, handleKeyUp } =
    useMultiplePaths(initialPaths, handleSave)

  const handleRestoreTemplatePath = useCallback(
    (path) => {
      const newPaths = [...paths, { type: 'templatePath', path }]
      handleSave(newPaths)
    },
    [paths, handleSave]
  )

  const handleToggle = (id) => setActiveOption(id)

  const onDragOver = useCallback((newPaths) => setPaths(newPaths), [setPaths])
  const onDragEnd = useCallback(() => handleSave(paths), [paths, handleSave])

  const { handleDragStart, handleDragEnter, handleDragEnd } = useDragAndDropPaths({ paths, onDragOver, onDragEnd })

  const excludedPaths = useMemo(() => {
    const allTemplatePaths = new Set(template.templatePaths || [])
    const usedTemplatePaths = new Set(template.paths?.filter((p) => p.type === 'templatePath').map((p) => p.path))
    return [...allTemplatePaths].filter((path) => !usedTemplatePaths.has(path))
  }, [template])

  return (
    <div className="section-divider">
      <div className="section-name dome-d-flex dome-align-center dome-justify-between">
        <span className="dome-p2 dome-color-darkest-grey dome-text-w500">fill</span>
        <ToggleSwitch onSelect={handleToggle} selected={activeOption} options={LIST_TOGGLE} />
      </div>
      <div className="dome-d-flex dome-flex-column dome-gap6">
        <div className="dome-d-flex dome-flex-column">
          <span className="dome-p3 dome-color-med-grey">Paths are filled in the order below.</span>
          <span className="dome-p3 dome-color-med-grey">
            Add any other relevant paths for this field, then reorder if necessary.
          </span>
        </div>
        {activeOption === 'list' && (
          <PathListItems
            paths={paths}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onChange={handleChange}
            removePath={handleRemove}
            addNewPath={() => handleAdd({ type: 'fillPath', path: '' })}
            onDragEnd={onDragEnd}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
          />
        )}
        {activeOption === 'code' && <TextArea component={component} />}
      </div>
      {excludedPaths.length ? (
        <div className="dome-d-flex dome-flex-column dome-gap6">
          <span className="dome-p3 dome-color-med-grey">excluded from fill</span>
          <div>
            {excludedPaths.map((path, index) => {
              return (
                <Input
                  key={path}
                  name={path}
                  value={path}
                  readOnly={true}
                  append={
                    <ActionIcon
                      readOnly={true}
                      onClick={() => handleRestoreTemplatePath(path)}
                      iconClassName="fa-solid fa-plus dome-color-aqua"
                    />
                  }
                />
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}
